import { useMemo, useRef, useState } from 'preact/hooks';
import type { Contribution, ReactionSummary, ThreadPhaseInventory } from '../api/types';
import { buildThreadedContributionData, groupByPhase, shouldMergeWithPrevious, UNPHASED_GROUP_KEY } from '../lib/contributionThreading';
import { mergePhasePromptGroups, resolvePhasePromptGroups, type PhasePromptGroup } from '../lib/phasePrompts';
import { ContributionCard } from './ContributionCard';
import { PhaseSection } from './PhaseSection';
import { ViewModeBar, type ViewMode } from './ViewModeBar';
import { DateSeparator } from './DateSeparator';
import { ReplyDrawer } from './ReplyDrawer';
import { PhaseJumpPill } from './PhaseJumpPill';
import styles from './ThreadedContributionList.module.css';

interface ThreadedContributionListProps {
  contributions: Contribution[];
  onBuildOn?: (contribution: Contribution) => void;
  // Reaction wiring (optional — read-only feeds omit these). When present, each
  // card gets its summary entry + a toggle handler keyed by contribution id.
  threadId?: string;
  reactionsByContribution?: ReactionSummary;
  onReact?: (contributionId: string, reactionType: string) => void;
  /**
   * The thread's phase inventory (optional — the fetch is non-fatal). When
   * present, byPhase sections sort in canonical template order and each phase
   * header shows the prompts its takes answered.
   */
  phaseInventory?: ThreadPhaseInventory | null;
}

const AUTHOR_COLORS = [
  'var(--st-author-1)', 'var(--st-author-2)', 'var(--st-author-3)',
  'var(--st-author-4)', 'var(--st-author-5)', 'var(--st-author-6)',
  'var(--st-author-7)', 'var(--st-author-8)', 'var(--st-author-9)',
  'var(--st-author-10)',
];

function getAuthorColor(id: string | null | undefined): string {
  if (!id) return AUTHOR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return AUTHOR_COLORS[Math.abs(hash) % AUTHOR_COLORS.length];
}

interface ContributorGroup {
  authorId: string;
  displayName: string;
  contributions: Contribution[];
}

export function ThreadedContributionList({ contributions, onBuildOn, threadId, reactionsByContribution, onReact, phaseInventory }: ThreadedContributionListProps) {
  const { ordered, childrenByParentId } = useMemo(
    () => buildThreadedContributionData(contributions),
    [contributions],
  );

  // Every derivation below answers "what is in this thread", so all of them run
  // over `ordered` (every visible take) rather than `roots` (reply structure
  // only). Grouping by roots hid riffs from the phase view entirely, and root
  // membership shifts as pages arrive, so a riff could appear and then vanish.
  //
  // Determine if any contributions have phases — if not, default to timeline
  // mode. Asking `roots` here meant a thread whose stamped takes were all riffs
  // fell back to timeline and rendered no phase sections at all.
  const hasPhases = useMemo(() => ordered.some((c) => c.templatePhase), [ordered]);
  const defaultMode: ViewMode = hasPhases ? 'byPhase' : 'timeline';

  const [viewMode, setViewMode] = useState<ViewMode>(defaultMode);
  const [collapsedPhases, setCollapsedPhases] = useState<Record<string, boolean>>({});
  const [collapsedAuthors, setCollapsedAuthors] = useState<Record<string, boolean>>({});
  const [replyDrawerTarget, setReplyDrawerTarget] = useState<Contribution | null>(null);
  const cardRefs = useRef(new Map<string, HTMLDivElement | null>());
  const phaseRefs = useRef(new Map<string, HTMLDivElement | null>());

  // Phase grouping — the inventory's templatePhaseKeys give the canonical
  // template order; without it, groups keep first-appearance order.
  const canonicalPhaseOrder = useMemo(
    () => phaseInventory?.phases.map((p) => p.templatePhaseKey),
    [phaseInventory],
  );
  const phaseGroups = useMemo(
    () => groupByPhase(ordered, canonicalPhaseOrder),
    [ordered, canonicalPhaseOrder],
  );

  // Resolved once per inventory rather than per section per render, so the
  // groups handed to PhaseSection keep a stable reference.
  const promptGroupsByKey = useMemo(() => {
    const byKey = new Map<string, PhasePromptGroup[]>();
    for (const phase of phaseInventory?.phases ?? []) {
      const resolved = resolvePhasePromptGroups(phase);
      const existing = byKey.get(phase.templatePhaseKey);
      // Two phases whose names share a 50-char prefix collapse onto one
      // templatePhaseKey server-side and their takes land in one section, so
      // merge their prompts rather than letting the later phase's header
      // silently replace the earlier one's.
      byKey.set(
        phase.templatePhaseKey,
        existing ? mergePhasePromptGroups(existing, resolved) : resolved,
      );
    }
    return byKey;
  }, [phaseInventory]);

  // Author grouping (retained from original)
  const contributorGroups = useMemo(() => {
    const groupMap = new Map<string, ContributorGroup>();
    const groupOrder: string[] = [];
    for (const contribution of ordered) {
      const authorId = contribution.authorId ?? '__anonymous__';
      const existing = groupMap.get(authorId);
      if (existing) {
        existing.contributions.push(contribution);
      } else {
        groupMap.set(authorId, {
          authorId,
          displayName: contribution.author?.displayName ?? 'Anonymous',
          contributions: [contribution],
        });
        groupOrder.push(authorId);
      }
    }
    return groupOrder.map((id) => groupMap.get(id)!);
  }, [ordered]);

  // Stats
  const totalAuthors = useMemo(() => {
    const authorIds = new Set(ordered.map((c) => c.authorId ?? '__anonymous__'));
    return authorIds.size;
  }, [ordered]);

  const togglePhaseCollapse = (phase: string) => {
    setCollapsedPhases((current) => ({ ...current, [phase]: !current[phase] }));
  };

  const toggleAuthorCollapse = (authorId: string) => {
    setCollapsedAuthors((current) => ({ ...current, [authorId]: !current[authorId] }));
  };

  const openReplies = (contribution: Contribution) => {
    setReplyDrawerTarget(contribution);
  };

  const jumpToParent = (parentId: string) => {
    const element = cardRefs.current.get(parentId);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const closeReplyDrawerAndJumpToParent = (parentId: string) => {
    setReplyDrawerTarget(null);
    jumpToParent(parentId);
  };

  const jumpToPhase = (phaseKey: string) => {
    const element = phaseRefs.current.get(phaseKey);
    if (!element) return;
    // Expand the phase if collapsed
    if (collapsedPhases[phaseKey]) {
      setCollapsedPhases((current) => ({ ...current, [phaseKey]: false }));
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderCard = (contribution: Contribution, index: number, list: Contribution[]) => {
    const replyCount = Math.max(
      contribution.buildOnChildCount ?? 0,
      (childrenByParentId.get(contribution.id) ?? []).length,
    );
    const compact = shouldMergeWithPrevious(contribution, list[index - 1]);

    return (
      <div key={contribution.id} class={styles.node}>
        <ContributionCard
          contribution={contribution}
          authorName={contribution.author?.displayName}
          onBuildOn={onBuildOn}
          onJumpToParent={jumpToParent}
          onToggleReplies={replyCount > 0 ? openReplies : undefined}
          replyCount={replyCount}
          compact={compact}
          authorColor={compact ? getAuthorColor(contribution.authorId) : undefined}
          threadId={threadId}
          reactions={reactionsByContribution?.[contribution.id]}
          onReact={onReact}
          cardRef={(element) => {
            cardRefs.current.set(contribution.id, element);
          }}
        />
      </div>
    );
  };

  // Date separator logic for timeline mode
  const renderTimelineView = () => {
    const elements: preact.ComponentChildren[] = [];
    let lastDate: string | null = null;

    for (let i = 0; i < ordered.length; i++) {
      const contribution = ordered[i];
      const dateKey = new Date(contribution.createdAt).toDateString();
      if (dateKey !== lastDate) {
        elements.push(<DateSeparator key={`date-${dateKey}`} date={contribution.createdAt} />);
        lastDate = dateKey;
      }
      elements.push(renderCard(contribution, i, ordered));
    }

    return elements;
  };

  // By-phase view
  const renderPhaseView = () => {
    // If only one phase, skip section wrapper
    if (phaseGroups.length <= 1 && phaseGroups[0]?.key === UNPHASED_GROUP_KEY) {
      return phaseGroups[0]?.contributions.map((c, i, list) => renderCard(c, i, list));
    }

    return phaseGroups.map((group) => {
      const isCollapsed = !!collapsedPhases[group.key];
      // Unstamped takes answered no phase prompt, so that bucket gets no header.
      const promptGroups = group.key === UNPHASED_GROUP_KEY
        ? undefined
        : promptGroupsByKey.get(group.key);

      return (
        <PhaseSection
          key={group.key}
          phase={group.phase}
          contributionCount={group.contributions.length}
          authors={group.authors}
          promptGroups={promptGroups}
          isCollapsed={isCollapsed}
          onToggle={() => togglePhaseCollapse(group.key)}
          sectionRef={(element) => {
            phaseRefs.current.set(group.key, element);
          }}
        >
          {group.contributions.map((c, i, list) => renderCard(c, i, list))}
        </PhaseSection>
      );
    });
  };

  // By-author view (original behavior)
  const renderAuthorView = () => {
    const needsGrouping = contributorGroups.length > 1 ||
      (contributorGroups.length === 1 && contributorGroups[0].contributions.length > 1);

    if (!needsGrouping) {
      return ordered.map((c, i) => renderCard(c, i, ordered));
    }

    return contributorGroups.map((group) => {
      const isCollapsed = !!collapsedAuthors[group.authorId];
      const hiddenCount = group.contributions.length - 1;
      const visibleContributions = isCollapsed
        ? [group.contributions[group.contributions.length - 1]]
        : group.contributions;

      return (
        <div key={group.authorId} class={styles.contributorGroup}>
          {group.contributions.length > 1 && (
            <button
              type="button"
              class={styles.contributorGroupToggle}
              onClick={() => toggleAuthorCollapse(group.authorId)}
            >
              <span class={styles.contributorGroupLabel}>
                {group.displayName} &middot; {group.contributions.length} {group.contributions.length === 1 ? 'take' : 'takes'}
              </span>
              <span class={styles.contributorGroupAction}>
                {isCollapsed ? `Show ${hiddenCount} more` : 'Collapse'}
                <span class={`${styles.contributorGroupChevron} ${isCollapsed ? '' : styles.contributorGroupChevronOpen}`}>&#9660;</span>
              </span>
            </button>
          )}
          {visibleContributions.map((c, i, list) => renderCard(c, i, list))}
        </div>
      );
    });
  };

  // Get replies for drawer
  const drawerReplies = replyDrawerTarget
    ? (childrenByParentId.get(replyDrawerTarget.id) ?? [])
    : [];

  return (
    <>
      <div class={styles.list}>
        <ViewModeBar
          mode={viewMode}
          onModeChange={setViewMode}
          totalContributions={ordered.length}
          totalAuthors={totalAuthors}
        />

        {viewMode === 'byPhase' && renderPhaseView()}
        {viewMode === 'timeline' && renderTimelineView()}
        {viewMode === 'byAuthor' && renderAuthorView()}
      </div>

      {viewMode === 'byPhase' && (
        <PhaseJumpPill
          phases={phaseGroups.map((g) => ({ key: g.key, phase: g.phase, count: g.contributions.length }))}
          onJumpTo={jumpToPhase}
        />
      )}

      {replyDrawerTarget && (
        <ReplyDrawer
          parent={replyDrawerTarget}
          replies={drawerReplies}
          onClose={() => setReplyDrawerTarget(null)}
          onBuildOn={onBuildOn}
          threadId={threadId}
          reactionsByContribution={reactionsByContribution}
          onReact={onReact}
          onJumpToParent={closeReplyDrawerAndJumpToParent}
        />
      )}
    </>
  );
}
